import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useState, useCallback, useEffect, Children } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import Tooltip from '../Tooltip/Tooltip.js';
import useOverflowIndicator from '../../hooks/useOverflowIndicator.js';
import useMutationObserver from '../../hooks/useMutationObserver.js';

const TEXT_PROPS = {
  element: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'strong', 'em', 'div'],
  size: ['xlarge', 'large', 'medium', 'small', 'xsmall', 'tiny', 'inherit'],
  type: ['body', 'display'],
  typeStyle: ['normal', 'bold', 'italic']
};
const textPropTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  element: PropTypes.oneOf(TEXT_PROPS.element),
  size: PropTypes.oneOf(TEXT_PROPS.size),
  type: PropTypes.oneOf(TEXT_PROPS.type),
  color: PropTypes.string,
  /** props that will be passed to the Tooltip component */
  tooltipProps: PropTypes.shape({
    content: PropTypes.node,
    position: PropTypes.string,
    isOpen: PropTypes.bool,
    disabled: PropTypes.bool
  }),
  /** shorthand for tooltipProps.content */
  tooltip: PropTypes.string,
  typeStyle: PropTypes.oneOf(TEXT_PROPS.typeStyle),
  title: PropTypes.string,
  ellipsized: PropTypes.bool,
  shouldShowTruncatedTooltip: PropTypes.bool,
  hideTitle: PropTypes.bool,
  truncatedTitle: PropTypes.string
};
const Text = ({
  children,
  className,
  element = 'span',
  type = 'body',
  typeStyle = 'normal',
  size = 'medium',
  color,
  title,
  tooltip,
  tooltipProps,
  ellipsized = false,
  shouldShowTruncatedTooltip = false,
  hideTitle = false,
  truncatedTitle,
  ...otherProps
}) => {
  const elementRef = useRef(null);
  const isOverflowing = useOverflowIndicator(elementRef, ellipsized);
  const [textContent, setTextContent] = useState(elementRef.current?.textContent);
  const tooltipContent = tooltip || tooltipProps?.content;
  const Element = element || 'span';
  const baseClass = 'nui-text';
  const typeClass = `nui-type-${type}--${size}`;
  const colorClass = color && `nui-type-color--${color}`;
  const styleClass = typeStyle !== 'normal' ? `nui-type-style--${typeStyle}` : false;
  const overflowClass = ellipsized ? 'nui-type-overflow--ellipsis' : '';
  const updateTextContent = useCallback(() => {
    setTextContent(elementRef.current?.textContent);
  }, []);
  useEffect(updateTextContent, []);
  useMutationObserver(elementRef, updateTextContent, {
    enabled: ellipsized,
    subtree: true,
    characterData: true
  });
  if (!Children.count(children)) {
    return null;
  }
  const getTruncatedTitle = () => {
    if (ellipsized && isOverflowing && !hideTitle && !shouldShowTruncatedTooltip && !tooltipContent) {
      return truncatedTitle ?? textContent;
    }
    return undefined;
  };
  const text = /*#__PURE__*/React__default.createElement(Element, _extends({
    className: cx(baseClass, typeClass, styleClass, colorClass, overflowClass, className),
    ref: elementRef,
    title: title ?? getTruncatedTitle()
  }, otherProps), children);
  return shouldShowTruncatedTooltip || tooltipContent ? /*#__PURE__*/React__default.createElement(Tooltip, _extends({}, tooltipProps, {
    content: tooltipContent || textContent,
    disabled: tooltipProps?.disabled || !isOverflowing && !tooltipContent
  }), text) : text;
};
Text.propTypes = textPropTypes;
Text.displayName = 'NuiText';
var Text$1 = Text;

export { TEXT_PROPS, Text$1 as default };
